import {
  FilterDTO,
  FilterOperator,
  ParkingSessionControlType,
  ParkingSubscriptionClient,
  ParkingSubscriptionDTO,
  ParkingSubscriptionType,
  SortDirection,
} from 'src/app/base/models/models.swagger.generated';
import { DialogUtility, TooltipModule } from '@syncfusion/ej2-angular-popups';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CreateZoneDTO, UpdateZoneDTO, ZoneDTO } from 'src/app/base/models/models.swagger.generated';
import { switchMap } from 'rxjs/operators';
import { DropDownConstantsFactory } from 'src/app/base/shared/syncfusion/dropdown-constants-factory';
import { ToastService } from 'src/app/base/services/toast.service';
import { FormHelper } from 'src/app/base/shared/form-helper';
import { ModalService } from 'src/app/base/shared/modal/modal.service';
import { ZoneDetailsService } from './zone-details.service';
import { AdministrationItemType, DetailsInitInfo } from '../../data-structures';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { YesNoGroupButtonComponent } from '../../../../../base/shared/yes-no-group-button/yes-no-group-button.component';
import { AppErrorMessagesComponent } from '../../../../../base/error-messages/app-error-messages.component';
import { NgClass } from '@angular/common';
import { MapBoxMapComponent, MapBoxMode, MapData } from "../../../../../base/shared/map-box-map/map-box-map.component";
import { TabModule } from '@syncfusion/ej2-angular-navigations';
import { ZoneDetailsBlacklistComponent } from './zone-details-blacklist/zone-details-blacklist.component';
import { ZoneEditFormType } from './zone-details-edit-form-type';
import { ButtonGroupComponent } from 'src/app/base/shared/button-group/button-group.component';

export interface ZoneInfo {
  selectedZone: ZoneDTO;
  parentZone: ZoneDTO;
}

@Component({
    selector: 'app-zone-details',
    templateUrl: './zone-details.component.html',
    styleUrls: ['./zone-details.component.scss'],
    imports: [TabModule, ReactiveFormsModule, NgClass, AppErrorMessagesComponent, YesNoGroupButtonComponent, DropDownListModule, TooltipModule, TranslateModule, MapBoxMapComponent, ZoneDetailsBlacklistComponent, ButtonGroupComponent]
})
export class ZoneDetailsComponent implements OnInit, OnDestroy {
  @ViewChild(MapBoxMapComponent) map: MapBoxMapComponent;
  @ViewChild(ZoneDetailsBlacklistComponent) blacklistComponent: ZoneDetailsBlacklistComponent;

  @Input() public set detailsInitInfo(value: DetailsInitInfo) {
    const valueStr = value ? JSON.stringify(value) : null;
    const detailsInitInfoStr = this._detailInitInfo ? JSON.stringify(this._detailInitInfo) : null;
    if (valueStr != detailsInitInfoStr) {
      this.zoneDetailsService.reset();
      this._detailInitInfo = value;
      if (value?.id) {
        this.zoneDetailsService.getZone(value.id).subscribe((zone: ZoneDTO) => {
          this._selectedZone = zone;
          this.addZone = false;
          if (!this._selectedZone.id) {
            // initialize parentId in case of create
            this._selectedZone.parentId = value.parentId;
          }
          this.loadFormData();
          if (this.parkingSubscriptions) {
            this.refreshParkingSubscriptionsDropdownValues();
            this.cdr.detectChanges();
          }
        });
      } else {
        this._selectedZone = null;
        this.loadFormData();
        this.addZone = true;
      }
    }
  }

  @Output() public cancel = new EventEmitter<void>();
  @Output() public zoneCreated = new EventEmitter<ZoneDTO>();
  @Output() public zoneUpdated = new EventEmitter<ZoneDTO>();
  @Output() public zoneDeleted = new EventEmitter<number>();
  @Output() public hasPendingDetailChangesChanged = new EventEmitter<boolean>();

  public dropdownSettings = DropDownConstantsFactory.createStandardClientDropdownSettings();
  public parkingSubscriptionsDropdownValues = [];
  public zoneForm: FormGroup<ZoneEditFormType>;
  public formChanged = false;
  public parkingSubscriptions: ParkingSubscriptionDTO[] = [];
  public ParkingSessionControlType = ParkingSessionControlType;

  public get isBusy(): boolean {
    return this.zoneDetailsService.isBusy;
  }

  public get isFormReady(): boolean {
    return !!this.zoneForm && (!!this._selectedZone || this.addZone);
  }

  public get isMapEnabled(): boolean {
    return !!this.zoneForm.value.enableGPSDetection;
  }

  public get isGroupingElement(): boolean {
    return !!this.zoneForm.getRawValue().groupingElement;
  }

  public get mapData(): MapData {
    return { data: this.zoneForm.controls.zoneGPSArea?.value, mode: MapBoxMode.GpsAreaMode };
  }

  public set mapData(value: MapData) {
    this.zoneForm.controls.zoneGPSArea.setValue(value?.data);
  }

  public get showUntouchedErrors(): boolean {
    return this._selectedZone !== null;
  }

  public get cancelButtonText(): string {
    if (this.formChanged) {
      return 'i18n.shared.cancel';
    }
    return 'i18n.shared.close';
  }

  public get hasCurrentParkerCountChanged(): boolean {
    if (!this._selectedZone || !this.zoneForm) {
      return false;
    }
    return this.zoneForm?.controls.currentParkerCount.valid && this._selectedZone?.currentParkerCount !== this.zoneForm?.controls.currentParkerCount.value;
  }

  public get isUpdateMode(): boolean {
    return !!this._selectedZone;
  }

  public get canDelete(): boolean {
    return this._selectedZone && !this._selectedZone.hasDevicesAssigned && !this._selectedZone.hasChildZones;
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  public tabAnimation: object = {
    previous: { effect: '', duration: 0, easing: '' },
    next: { effect: '', duration: 0, easing: '' },
  };

  MapBoxMode = MapBoxMode;

  private addZone = false;
  private formInitialValue;
  private _selectedZone: ZoneDTO = null;
  private subscriptions = new Subscription();
  private _detailInitInfo: DetailsInitInfo;

  constructor(private zoneDetailsService: ZoneDetailsService,
    private translateService: TranslateService,
    private parkingSubscriptionClient: ParkingSubscriptionClient,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private toastService: ToastService,
    private modalService: ModalService) {
    this.zoneForm = this.fb.group<ZoneEditFormType>({
      id: this.fb.control<number>(null),
      name: this.fb.control<string>(null, Validators.required),
      groupingElement: this.fb.control(false),
      separateNFCCode: this.fb.control(false),
      nFCCode: this.fb.control<string>({ value: null, disabled: true }, Validators.required),
      enableGPSDetection: this.fb.control(false),
      zoneGPSArea: this.fb.control<string>({ value: null, disabled: true }, Validators.required),
      foreignParkersAllowed: this.fb.control(false),
      maxPassageTime: this.fb.control<number>(null, [Validators.required, Validators.min(0)]),
      foreignParkingSubscriptionId: this.fb.control<number>({ value: null, disabled: true }, Validators.required),
      fastCheckinSubscriptionId: this.fb.control<number>({ value: null, disabled: true }),
      parkingSpaceNumberDefinition: this.fb.control<string>(null),
      parkingSpaceCapacity: this.fb.control<number>(null, [Validators.min(0)]),
      currentParkerCount: this.fb.control(0, [Validators.min(0)]),
      useBlacklistArea: this.fb.control(false),
      blacklistGPSArea: this.fb.control<string>({ value: null, disabled: true }),
      sessionControlType: this.fb.control<ParkingSessionControlType>(null, Validators.required)
    });

    this.subscriptions.add(this.zoneForm.valueChanges.subscribe((v) => {
      var hasChanges = false;
      if (this.formInitialValue) {
        hasChanges = !(JSON.stringify(this.formInitialValue) === JSON.stringify(this.zoneForm.getRawValue()));
        //Log form changes to console for debugging purposes
        // this.logFormChanges(this.formInitialValue, this.zoneForm.getRawValue());
      }
      this.updateFormChangedState(hasChanges);
    }));
    this.subscriptions.add(this.zoneForm.controls.foreignParkersAllowed.valueChanges.subscribe(value => {
      if (!value) {
        this.zoneForm.controls.foreignParkingSubscriptionId.setValue(null);
        this.zoneForm.controls.fastCheckinSubscriptionId.setValue(null);
      }
      this.setFieldEnabledStatus(this.zoneForm.controls.foreignParkingSubscriptionId, value);
      if (this.zoneForm.controls.sessionControlType.value === ParkingSessionControlType.CheckInCheckOut) {
        this.setFieldEnabledStatus(this.zoneForm.controls.fastCheckinSubscriptionId, value);
      }
    }));
    this.subscriptions.add(this.zoneForm.controls.separateNFCCode.valueChanges.subscribe(value => {
      this.setFieldEnabledStatus(this.zoneForm.controls.nFCCode, value);
    }));
    this.subscriptions.add(this.zoneForm.controls.sessionControlType.valueChanges.subscribe(value => {
      if (value === ParkingSessionControlType.HardwareControlled) {
        this.zoneForm.controls.fastCheckinSubscriptionId.setValue(null);
        this.setFieldEnabledStatus(this.zoneForm.controls.fastCheckinSubscriptionId, false);
      } else {
        this.setFieldEnabledStatus(this.zoneForm.controls.fastCheckinSubscriptionId, this.zoneForm.controls.foreignParkersAllowed.value);
      }
    }));

    this.subscriptions.add(this.zoneForm.controls.groupingElement.valueChanges.subscribe(value => {
      if (value) {
        this.zoneForm.controls.maxPassageTime.setValidators([]);
      } else {
        this.zoneForm.controls.maxPassageTime.setValidators([Validators.required]);
      }
      this.zoneForm.controls.maxPassageTime.updateValueAndValidity();
    }));
    this.subscriptions.add(this.zoneForm.controls.currentParkerCount.valueChanges.subscribe(value => {
      if (this.formInitialValue) {
        this.formInitialValue.currentParkerCount = value;
        const hasChanges = !(JSON.stringify(this.formInitialValue) === JSON.stringify(this.zoneForm.getRawValue()));
        //Log form changes to console for debugging purposes
        // this.logFormChanges(this.formInitialValue, this.zoneForm.getRawValue());
        this.updateFormChangedState(hasChanges);
      }
    }));
  }

  public ngOnInit(): void {
    this.loadParkingSubscriptions();

    this.zoneForm.controls.enableGPSDetection.valueChanges.subscribe(value => {
      if (!value) {
        this.zoneForm.controls.zoneGPSArea.setValue(null);
      }
    });
  }

  public ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  public onSelectForeignParkingSubscription(args): void {
    args.cancel = args.itemData.disabled;
  }

  public onCancelClicked() {
    this.cancel.emit();
  }

  public submitForm() {
    const data = this.getDTOFromFormData();
    if (this._selectedZone != null) {
      this.zoneDetailsService.updateZone(data as UpdateZoneDTO).subscribe((zone: ZoneDTO) => {
        this._selectedZone = zone;
        this.loadFormData();
        // avoid timing issues with has changes event update and zone updated event
        setTimeout(() => { this.zoneUpdated.emit(zone); }, 0);
        this.map?.lockMap();
        this.blacklistComponent?.lockMap();
      });
    } else {
      this.zoneDetailsService.createZone(data as CreateZoneDTO).subscribe((zone: ZoneDTO) => {
        this.zoneCreated.emit(zone);
        this.closeForm();
      });
    }
  }

  public onDeleteZone() {
    const dialog = this.modalService.showOkCancelDialog('i18n.core.modal.delete.title', 'i18n.core.modal.delete.content');
    dialog.onOkSubject.subscribe(() => {
      this.zoneDetailsService.deleteZone(this._selectedZone.id).subscribe(data => {
        const zoneId = this._selectedZone.id;
        this.closeForm();
        this.zoneDeleted.emit(zoneId);
      });
    });
  }

  public updateParkingSpaceCounter(): void {
    if (!this.hasCurrentParkerCountChanged) {
      return;
    }
    const dialog = this.modalService.showOkCancelDialog(
      'i18n.domain.administration.zone-details.zone-details-form.change-parking-space-counter-dialog-header',
      'i18n.domain.administration.zone-details.zone-details-form.change-parking-space-counter-dialog-description'
    );
    dialog.onOkSubject.pipe(
      switchMap(() => this.zoneDetailsService.setCurrentParkerCount(this._selectedZone.id, this.zoneForm.controls.currentParkerCount.value)),
    )
      .subscribe((zone: ZoneDTO) => {
        this._selectedZone.currentParkerCount = zone.currentParkerCount;
        this.zoneForm.controls.currentParkerCount.markAsPristine();
        this.toastService.showSuccess(
          'i18n.domain.administration.zone-details.zone-details-form.parking-space-counter-success-title',
          'i18n.domain.administration.zone-details.zone-details-form.parking-space-counter-success-description'
        );
      });
  }

  //Deactivate swipe of tabgroup
  public tabSelect(event): void {
    if (event.isSwiped) {
      event.cancel = true;
    }
  }

  public onBlacklistDataChange(args:any){
    this.zoneForm.controls.useBlacklistArea.setValue(args.useBlacklistArea);
    this.zoneForm.controls.blacklistGPSArea.setValue(args.blacklistArea);
    this.zoneForm.controls.blacklistGPSArea.markAsTouched();
    this.zoneForm.controls.useBlacklistArea.markAsTouched();
    this.zoneForm.controls.blacklistGPSArea.updateValueAndValidity();
    this.zoneForm.controls.useBlacklistArea.updateValueAndValidity();
  }

  private closeForm() {
    this.addZone = false;
    this._selectedZone = null;
  }

  private loadFormData() {
    if (this._selectedZone) {
      this.zoneForm.reset({
        id: this._selectedZone.id,
        name: this._selectedZone.name ?? '',
        groupingElement: this._selectedZone.isGroupingElement,
        separateNFCCode: this._selectedZone.separateNFCCode,
        nFCCode: this._selectedZone.nfcCode ?? '',
        enableGPSDetection: this._selectedZone.enableGPSDetection,
        zoneGPSArea: this._selectedZone.zoneGPSArea ?? null,
        foreignParkersAllowed: this._selectedZone.foreignParkersAllowed,
        maxPassageTime: this._selectedZone.maxPassageTime,
        foreignParkingSubscriptionId: this._selectedZone.foreignParkingSubscription?.id,
        fastCheckinSubscriptionId: this._selectedZone.fastCheckinSubscription?.id,
        parkingSpaceNumberDefinition: this._selectedZone.parkingSpaceNumberDefinition,
        parkingSpaceCapacity: this._selectedZone.parkingSpaceCapacity,
        currentParkerCount: this._selectedZone.currentParkerCount,
        useBlacklistArea: this._selectedZone.useBlacklistArea,
        blacklistGPSArea: this._selectedZone.blacklistGPSArea,
        sessionControlType: this._selectedZone.sessionControlType
      }, { onlySelf: true });
      this.setFieldEnabledStatus(this.zoneForm.controls.nFCCode, this._selectedZone.separateNFCCode);
      this.setFieldEnabledStatus(this.zoneForm.controls.foreignParkingSubscriptionId, this._selectedZone.foreignParkersAllowed);
      this.setFieldEnabledStatus(this.zoneForm.controls.foreignParkersAllowed, true);
      this.zoneForm.controls.nFCCode.markAsTouched();
      this.zoneForm.controls.zoneGPSArea.markAsTouched();
      this.zoneForm.controls.foreignParkingSubscriptionId.markAsTouched();
    } else {
      this.zoneForm.reset({ currentParkerCount: 0, foreignParkersAllowed: false, sessionControlType: ParkingSessionControlType.CheckInCheckOut, maxPassageTime: 0 });
      this.setFieldEnabledStatus(this.zoneForm.controls.foreignParkersAllowed, false);
    }
    this.formInitialValue = this.zoneForm.getRawValue();
    this.updateFormChangedState(false);
    FormHelper.updateTreeValidity(this.zoneForm, !!this.zoneForm.controls.name.value);
  }

  private setFieldEnabledStatus(control: AbstractControl, isEnabled: boolean) {
    if (isEnabled) {
      control.enable();
    } else {
      control.disable();
    }
  }

  private getDTOFromFormData(): CreateZoneDTO | UpdateZoneDTO {
    const rawValue = this.zoneForm.getRawValue();
    let dto: CreateZoneDTO | UpdateZoneDTO;
    if (this._selectedZone != null) {
      dto = new UpdateZoneDTO({
        id: this._selectedZone.id,
        name: rawValue.name,
        isGroupingElement: rawValue.groupingElement ?? false,
        separateNFCCode: rawValue.separateNFCCode,
        nfcCode: rawValue.nFCCode,
        enableGPSDetection: rawValue.enableGPSDetection,
        zoneGPSArea: this.zoneForm.getRawValue().zoneGPSArea,
        foreignParkersAllowed: rawValue.foreignParkersAllowed,
        maxPassageTime: rawValue.maxPassageTime ?? 0,
        foreignParkingSubscriptionId: rawValue.foreignParkingSubscriptionId,
        fastCheckinSubscriptionId: rawValue.fastCheckinSubscriptionId,
        parkingSpaceNumberDefinition: rawValue.parkingSpaceNumberDefinition,
        parkingSpaceCapacity: rawValue.parkingSpaceCapacity,
        useBlacklistArea: rawValue.useBlacklistArea ?? false,
        blacklistGPSArea: rawValue.blacklistGPSArea,
        sessionControlType: rawValue.sessionControlType
      });
    } else {
      dto = new CreateZoneDTO({
        parentZoneId: this._detailInitInfo.parentType === AdministrationItemType.Zone ? this._detailInitInfo.parentId : null,
        clientId: this._detailInitInfo.clientId,
        name: rawValue.name,
        isGroupingElement: rawValue.groupingElement ?? false,
        separateNFCCode: rawValue.separateNFCCode ?? false,
        nfcCode: rawValue.nFCCode,
        enableGPSDetection: rawValue.enableGPSDetection ?? false,
        zoneGPSArea: this.zoneForm.getRawValue().zoneGPSArea,
        maxPassageTime: rawValue.maxPassageTime ?? 0,
        parkingSpaceNumberDefinition: rawValue.parkingSpaceNumberDefinition,
        parkingSpaceCapacity: rawValue.parkingSpaceCapacity,
        currentParkerCount: rawValue.currentParkerCount,
        useBlacklistArea: rawValue.useBlacklistArea ?? false,
        blacklistGPSArea: rawValue.blacklistGPSArea,
        sessionControlType: rawValue.sessionControlType
      });
    }
    return dto;
  }

  private loadParkingSubscriptions(): void {
    const filters = [
      new FilterDTO({
        isComplex: false,
        field: 'Type',
        operator: FilterOperator.Equal,
        value: ParkingSubscriptionType.ForeignParkerSubscription.toString(),
      }),
      new FilterDTO({
        isComplex: false,
        field: 'Active',
        operator: FilterOperator.Equal,
        value: 'true',
      }),
    ];
    this.parkingSubscriptionClient.searchParkingSubscriptions(this._detailInitInfo.clientId, filters, 0, 0, 'Name', SortDirection.Ascending)
      .subscribe(items => {
        this.parkingSubscriptions = items.items;
        if (this._selectedZone) {
          this.refreshParkingSubscriptionsDropdownValues();
        }
      });
  }

  private updateFormChangedState(hasChanges: boolean) {
    if (this.formChanged !== hasChanges) {
      this.formChanged = hasChanges;
      this.hasPendingDetailChangesChanged.emit(hasChanges);
    }
  }

  private refreshParkingSubscriptionsDropdownValues() {
    this.parkingSubscriptionsDropdownValues = this.parkingSubscriptions
      // display all assignable subs, or if the current selected sub is not assignable, that as well
      .filter(item => (item.assignable && item.tariffs.some((t) => this._selectedZone.assignedTariffs.some(tid => tid === t.id))) || item.id === this.zoneForm.value.foreignParkingSubscriptionId)
      .map(item => ({ name: item.name, id: item.id, disabled: !item.assignable }));
  }

  private logFormChanges(initialValue: any, currentValue: any): void {
    const changes = this.getFormChanges(initialValue, currentValue);
    console.log('Form changes:', changes);
  }

  private getFormChanges(initialValue: any, currentValue: any): any {
    const changes: any = {};
    Object.keys(currentValue).forEach(key => {
      if (initialValue[key] !== currentValue[key]) {
        changes[key] = {
          initialValue: initialValue[key],
          currentValue: currentValue[key]
        };
      }
    });
    return changes;
  }
}
